import { create } from 'zustand'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'osprey-theme'

/** Resolve the initial theme: explicit user choice wins, else follow the OS preference. */
const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** Toggle the `dark` class on <html> — Tailwind's `darkMode: 'class'` reads from here. */
const applyTheme = (theme: Theme) => {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', theme === 'dark')
}

type ThemeState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: getInitialTheme(),
  setTheme: (theme) => {
    localStorage.setItem(STORAGE_KEY, theme)
    applyTheme(theme)
    set({ theme })
  },
  toggleTheme: () => {
    get().setTheme(get().theme === 'dark' ? 'light' : 'dark')
  },
}))

/**
 * Apply the persisted/system theme to <html> before React's first paint.
 * Call once from main.tsx so there's no flash of the wrong theme (FOUC).
 */
export const initTheme = () => applyTheme(getInitialTheme())
